import React, { FC, useCallback, useEffect, useState } from 'react'
import { Token } from '@getgo/auth-client'
import { useLocalStorage } from '@rehooks/local-storage'
import EventSource from 'eventsource'

import { LiveJobsContext } from 'components'
import { useAccountContext } from 'components/contexts/AccountContext'
import config from 'config'
import { Job } from 'types/Job'
import { fetchJobs } from 'utils'
import { getJobGlobalEvent } from 'utils/jobs/getJobGlobalEvent'
import { isJobMetadataRequired } from 'utils/jobs/isJobMetadataRequired'

const LiveJobsProvider: FC = ({ children }) => {
  const [authToken] = useLocalStorage<Token>(config.AUTH_TOKEN_KEY)
  const { accountKey: selectedAccountKey } = useAccountContext()
  const [eventSource, setEventSource] = useState<EventSource | null>(null)
  const [liveJob, setLiveJob] = useState<Job | null>(null)

  const jobHandler = useCallback(
    async (event: Event) => {
      const baseJob: Job = JSON.parse((event as MessageEvent).data)
      // If metadata is required we need to fetch this job again
      const job = isJobMetadataRequired(baseJob)
        ? ((await fetchJobs({ accountKey: selectedAccountKey!, jobId: baseJob.id })) as Job)
        : baseJob

      setLiveJob(job)

      // TODO we should probably not send global events to sub-apps
      const globalEvent = getJobGlobalEvent(job)
      if (globalEvent) {
        window.dispatchEvent(new CustomEvent(globalEvent))
      }
    },
    [selectedAccountKey],
  )

  useEffect(() => {
    if (selectedAccountKey && authToken?.access_token) {
      if (eventSource) {
        eventSource.close()
        setLiveJob(null)
      }
      const path = `${config.EXTERNAL_ADMIN_URL}/ext-admin/rest/accounts/${selectedAccountKey}/jobs`
      const jobEventSource = new EventSource(path, { headers: { Authorization: `Bearer ${authToken.access_token}` } })

      jobEventSource.addEventListener('ready', jobHandler)
      jobEventSource.addEventListener('completed', jobHandler)
      jobEventSource.addEventListener('failed', jobHandler)

      setEventSource(jobEventSource)
    }

    return () => {
      if (eventSource) {
        eventSource.close()
        setLiveJob(null)
      }
    }
    // eslint-disable-next-line
  }, [selectedAccountKey, authToken?.access_token])

  return <LiveJobsContext.Provider value={liveJob}>{children}</LiveJobsContext.Provider>
}

export default LiveJobsProvider
